import { Industry } from '../types/types';
import { randomFromArray } from '../utils/utils';

// Industry-specific project types
export const industryProjectTypes: Record<Industry, string[]> = {
  'Computers and Electronics': [
    'Hardware Modernization',
    'Cloud Migration',
    'Edge Computing',
    'Semiconductor Solutions',
  ],
  'Financial Services': [
    'Fraud Detection System',
    'Digital Banking Platform',
    'Regulatory Compliance',
    'Financial Analytics',
  ],
  Healthcare: [
    'Patient Data Platform',
    'Healthcare Analytics',
    'Telehealth Solution',
    'Medical Records System',
  ],
  'Software and Internet': [
    'SaaS Platform',
    'API Modernization',
    'DevOps Optimization',
    'Serverless Architecture',
  ],
  Telecommunications: [
    '5G Network Implementation',
    'Network Infrastructure',
    'Telecommunications Analytics',
    'Unified Communications',
  ],
  'Transportation and Logistics': [
    'Fleet Management System',
    'Supply Chain Optimization',
    'Logistics Analytics',
    'Tracking Solution',
  ],
  'Energy - Power and Utilities': [
    'Smart Grid Implementation',
    'Energy Management System',
    'Renewable Resources Platform',
    'Utility Analytics',
  ],
  Government: [
    'Citizen Services Portal',
    'Secure Data Platform',
    'Compliance System',
    'Government Analytics',
  ],
};

// General project types applicable to any industry
export const generalProjectTypes = [
  'Digital Transformation',
  'Analytics Platform',
  'Infrastructure Modernization',
  'Data Platform',
  'AI/ML Implementation',
  'Security Enhancement',
];

// Industry-specific business problems
export const businessProblems: Record<Industry, string[]> = {
  'Computers and Electronics': [
    'Need to modernize hardware infrastructure',
    'Seeking improved computing capabilities',
    'Require edge computing solutions',
    'Addressing semiconductor supply chain issues',
  ],
  'Financial Services': [
    'Need for fraud detection enhancements',
    'Seeking regulatory compliance solutions',
    'Addressing digital banking security',
    'Requiring financial analytics capabilities',
  ],
  Healthcare: [
    'Need to modernize patient systems',
    'Seeking telehealth capabilities',
    'Addressing health data security compliance',
    'Improving medical records accessibility',
  ],
  'Software and Internet': [
    'Need to modernize API architecture',
    'Seeking serverless capabilities',
    'Addressing DevOps pipeline inefficiencies',
    'Improving application performance',
  ],
  Telecommunications: [
    'Need to implement 5G infrastructure',
    'Seeking network analytics capabilities',
    'Addressing communications security',
    'Improving network performance',
  ],
  'Transportation and Logistics': [
    'Need for fleet management optimization',
    'Seeking supply chain visibility',
    'Addressing logistics tracking challenges',
    'Improving delivery performance',
  ],
  'Energy - Power and Utilities': [
    'Need for smart grid implementation',
    'Seeking renewable energy management',
    'Addressing utility analytics challenges',
    'Improving energy distribution efficiency',
  ],
  Government: [
    'Need for secure citizen services',
    'Seeking compliance system improvements',
    'Addressing data security requirements',
    'Improving government service accessibility',
  ],
};

// Generate industry-specific engagement title
export const generateEngagementTitle = (
  companyName: string,
  industry: Industry,
): string => {
  const industrySpecificTypes = industryProjectTypes[industry];
  const allProjectTypes = [...industrySpecificTypes, ...generalProjectTypes];
  const projectType = randomFromArray(allProjectTypes);
  return `${companyName} - ${projectType} Initiative`;
};

// Get business problem for specific industry
export const getBusinessProblemForIndustry = (industry: Industry): string => {
  return randomFromArray(businessProblems[industry]);
};
